import { Controller } from "@hotwired/stimulus"
import { Tabs } from "flowbite"

// Connects to data-controller="flowbite-tabs"
export default class extends Controller {
  static targets = ["tab"]
  static values = {
    activeClasses: { type: String, default: "tg-primary-border tg-primary-text" },
    inactiveClasses: { type: String, default: "bg-white hover:text-gray-600 hover:bg-gray-50 hover:border-b-2 hover:border-b-gray-400" },
    defaultTab: { type: String, default: "" },
    syncUrl: { type: Boolean, default: false },
    urlParam: { type: String, default: "tab" }
  }

  connect() {
    const parentElement = this.element
    this.tabElements = this.tabTargets.map((tab) => {
      return {
        id: tab.dataset.tabObjectId,
        triggerEl: document.getElementById(tab.dataset.tabTriggerId),
        targetEl: document.getElementById(tab.dataset.tabTargetId)
      }
    })

    const defaultTabId = this.resolveDefaultTabId(this.tabElements)
    this.skipUrlSync = true

    const options = {
      defaultTabId,
      activeClasses: this.activeClassesValue,
      inactiveClasses: this.inactiveClassesValue,
      onShow: (tabsInstance) => {
        const activeTabId = tabsInstance.getActiveTab().id
        this.dispatch("updateStore", { detail: { content: activeTabId } })
        if (this.syncUrlValue && !this.skipUrlSync) this.updateUrl(activeTabId)
      }
    }

    this.tabs = new Tabs(parentElement, this.tabElements, options)
    this.tabs.show(defaultTabId, true)
    this.skipUrlSync = false

    if (this.syncUrlValue) {
      this.beforeCacheHandler = this.syncDomForCache.bind(this)
      document.addEventListener("turbo:before-cache", this.beforeCacheHandler)
    }
  }

  disconnect() {
    if (this.beforeCacheHandler) {
      document.removeEventListener("turbo:before-cache", this.beforeCacheHandler)
    }
  }

  changeActiveTab({ detail: { content } }) {
    this.tabs.show(content)
  }

  resolveDefaultTabId(tabElements) {
    if (this.syncUrlValue) {
      const urlTab = new URLSearchParams(window.location.search).get(this.urlParamValue)
      if (urlTab && tabElements.some((t) => t.id === urlTab)) return urlTab
    }

    if (this.defaultTabValue && tabElements.some((t) => t.id === this.defaultTabValue)) {
      return this.defaultTabValue
    }

    return this.tabTargets[0].dataset.tabObjectId
  }

  updateUrl(tabId) {
    const url = new URL(window.location.href)
    url.searchParams.set(this.urlParamValue, tabId)
    history.replaceState({}, "", url)
  }

  syncDomForCache() {
    const activeId = this.tabs?.getActiveTab()?.id
    if (!activeId) return

    this.element.dataset.flowbiteTabsDefaultTabValue = activeId

    this.tabElements.forEach(({ id, targetEl, triggerEl }) => {
      if (!targetEl || !triggerEl) return

      const isActive = id === activeId
      targetEl.classList.toggle("hidden", !isActive)

      if (isActive) {
        triggerEl.classList.add(...this.activeClassesValue.split(" "))
        triggerEl.classList.remove(...this.inactiveClassesValue.split(" "))
        triggerEl.setAttribute("aria-selected", "true")
      } else {
        triggerEl.classList.remove(...this.activeClassesValue.split(" "))
        triggerEl.classList.add(...this.inactiveClassesValue.split(" "))
        triggerEl.setAttribute("aria-selected", "false")
      }
    })
  }
}
